import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "../../services/api";

const buildToken = (headers = {}) => {
  const raw = headers.Authorization || headers.authorization || "";
  return raw.replace(/Bearer\s+/i, "");
};

// kind : « equipe » (sprints, tokens, responsables) ou « lancement » (réunion en direct d'un projet en proposition :
// son capté, proposition de Nexus, clôture qui passe le projet en cours).
// enabled : false tant qu'il ne faut pas ouvrir de kickoff (projet pas encore chargé, déjà en cours).
const useKickoffSession = ({ projectId, user, getAuthHeaders, kind = "equipe", enabled = true }) => {
  const [kickoffSession, setKickoffSession] = useState(null);
  const [presenceCount, setPresenceCount] = useState(0);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [kickoffError, setKickoffError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [live, setLive] = useState(null); // { active, started_by, words, warning, mac_connected }
  const [draft, setDraft] = useState(null); // proposition de Nexus et valeurs écartées
  const [draftStatus, setDraftStatus] = useState({ updating: false, error: null });
  const [finished, setFinished] = useState(null); // kickoff de lancement clos : { by }
  const socketRef = useRef(null);
  const rejoinTimeoutRef = useRef(null);
  const finishedRef = useRef(false);
  const clientIdRef = useRef(`kickoff-${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const createOrJoinKickoff = useCallback(async () => {
    if (!projectId || !enabled || finishedRef.current) {
      if (!projectId) setKickoffSession(null);
      return;
    }
    try {
      setKickoffError(null);
      const created = await apiService.post("/kickoffs", { project_id: projectId, kind });
      setKickoffSession(created || null);
    } catch (err) {
      setKickoffError(err?.message || "Impossible de demarrer le kickoff.");
    }
  }, [projectId, kind, enabled]);

  const scheduleRejoin = useCallback(() => {
    if (!projectId) return;
    if (rejoinTimeoutRef.current) return;
    rejoinTimeoutRef.current = setTimeout(() => {
      rejoinTimeoutRef.current = null;
      createOrJoinKickoff();
    }, 250);
  }, [createOrJoinKickoff, projectId]);

  useEffect(() => {
    createOrJoinKickoff();
  }, [createOrJoinKickoff]);

  const sendKickoffRealtime = useCallback((payload) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const notifyRefresh = useCallback(
    (scope) => {
      const senderId = user?.id ?? user?.employee_id ?? null;
      return sendKickoffRealtime({
        type: "refresh",
        scope,
        sender_id: senderId,
        sender_client_id: clientIdRef.current,
      });
    },
    [sendKickoffRealtime, user]
  );

  useEffect(() => {
    const kickoffId = kickoffSession?.id;
    const authHeaders = getAuthHeaders ? getAuthHeaders() : apiService.getHeaders();
    const token = buildToken(authHeaders);
    if (!kickoffId || !token) {
      setRealtimeConnected(false);
      setPresenceCount(0);
      setConnectedUsers([]);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return;
    }

    const wsBase = apiService.getBaseUrl().replace(/^http/i, "ws");
    const socket = new WebSocket(`${wsBase}/kickoffs/${kickoffId}/ws?token=${encodeURIComponent(token)}`);
    socketRef.current = socket;

    socket.onopen = () => setRealtimeConnected(true);
    socket.onclose = (event) => {
      setRealtimeConnected(false);
      setPresenceCount(0);
      setConnectedUsers([]);
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      const reason = event?.reason || "";
      if (reason.toLowerCase().includes("kickoff")) {
        scheduleRejoin();
      }
    };
    socket.onerror = () => setRealtimeConnected(false);
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "sync_state") {
          if (data.kickoff) setKickoffSession(data.kickoff);
          if (typeof data.connected === "number") setPresenceCount(data.connected);
          if (Array.isArray(data.connected_users)) setConnectedUsers(data.connected_users);
          if (data.live) setLive(data.live);
          if (data.draft) setDraft(data.draft);
          if (data.draft_status) setDraftStatus(data.draft_status);
        } else if (data.type === "live_status") {
          const { type, ...rest } = data;
          setLive((prev) => ({ ...(prev || {}), ...rest }));
        } else if (data.type === "draft") {
          setDraft(data.draft || null);
          if (data.draft_status) setDraftStatus(data.draft_status);
        } else if (data.type === "draft_status") {
          setDraftStatus({ updating: Boolean(data.updating), error: data.error || null });
        } else if (data.type === "presence") {
          setPresenceCount(typeof data.connected === "number" ? data.connected : 0);
          if (Array.isArray(data.users)) setConnectedUsers(data.users);
        } else if (data.type === "refresh") {
          const senderClientId = data.sender_client_id;
          if (senderClientId && String(senderClientId) === String(clientIdRef.current)) {
            return;
          }
          setRefreshToken((prev) => prev + 1);
        } else if (data.type === "kickoff_closed" && data.finished) {
          finishedRef.current = true;
          setFinished({ by: data.by || null });
        } else if (data.type === "kickoff_closed") {
          setKickoffSession(null);
          setPresenceCount(0);
          setConnectedUsers([]);
          scheduleRejoin();
        }
      } catch (err) {
        console.error("WebSocket parse error", err);
      }
    };

    return () => {
      try {
        socket.close();
      } catch (err) {
        // ignore
      }
    };
  }, [kickoffSession?.id, getAuthHeaders, user, scheduleRejoin]);

  useEffect(() => {
    return () => {
      if (rejoinTimeoutRef.current) {
        clearTimeout(rejoinTimeoutRef.current);
      }
    };
  }, []);

  return {
    kickoffSession,
    presenceCount,
    connectedUsers,
    realtimeConnected,
    kickoffError,
    refreshToken,
    notifyRefresh,
    live,
    draft,
    setDraft,
    draftStatus,
    finished,
  };
};

export default useKickoffSession;
